import {
  Grid,
  Box,
  FormControl,
  Checkbox,
  Typography,
  FormControlLabel,
  Autocomplete,
  TextField,
  Tooltip,
  Divider,
} from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { styled, useTheme } from '@mui/material/styles';
import { NotificationsForm } from './NotificationSettingsModal';
import { useMemo } from 'react';
import { useBuildWiseModules } from '@buildwise/libs/helpers';
import QuestionFillIcon from 'remixicon-react/QuestionFillIcon';
import ChatQuoteFillIcon from 'remixicon-react/ChatQuoteFillIcon';
import FileListFillIcon from 'remixicon-react/FileListFillIcon';
import InformationLineIcon from 'remixicon-react/InformationLineIcon';
import useUserData from 'hooks/useUserData';
import { useSuggestionContactsQuery } from '@buildwise/libs/company-admin';
import MailLineIcon from 'remixicon-react/MailLineIcon';
import SmartphoneLineIcon from 'remixicon-react/SmartphoneLineIcon';
import { ConfigEntryType } from 'config/config';
import { FieldWrapper } from '@buildwise/libs/people';
import RenderFilterFieldByType from '@buildwise/libs/feed/components/RenderFilterFieldByType';

const INTERACTION_NOTIFICATIONS = {
  notifyWhenOthersCommentOnMyArticlesFlag: 'Notify me for comments on my posts',
  notifyWhenOthersReplyToMyCommentsFlag: 'Notify me for replies to my comments',
};

const notificationOptions = [
  { label: 'Real-time', value: 'REAL_TIME' },
  { label: 'Daily', value: 'DAILY' },
  { label: 'Weekly', value: 'WEEKLY' },
  { label: 'Never', value: 'NEVER' },
];

function InteractionsForm({ requiredInputFields }: { requiredInputFields: ConfigEntryType[] }) {
  const {
    control,
    formState: { isSubmitting },
    setValue,
  } = useFormContext<NotificationsForm>();
  const { user, companyId } = useUserData();
  const { suggestionContacts } = useSuggestionContactsQuery({
    companyId,
  });
  const { homeModuleFeatures } = useBuildWiseModules();
  const theme = useTheme();

  const notifications = useWatch({
    control,
  });

  const isUserExist = useMemo(() => {
    return suggestionContacts.some((contact) => contact?.owner === user?.username);
  }, [suggestionContacts]);

  const activeNotificationsList = useMemo(() => {
    const list = {
      Posts: {
        isActive: homeModuleFeatures['Share Knowledge']?.isActive,
        key: 'postNotificationFrequency',
        icon: <FileListFillIcon color={theme.palette.custom.pirateGold} size={18} />,
        title: '',
      },
      Questions: {
        isActive: homeModuleFeatures['Ask Peers']?.isActive,
        key: 'questionNotificationFrequency',
        icon: <QuestionFillIcon color={theme.palette.custom.dodgerBlue} size={18} />,
        title: 'For the teams you belong to',
      },
    };
    if (isUserExist) {
      list['Suggestions'] = {
        isActive: homeModuleFeatures['Suggest Improvements']?.isActive,
        key: 'suggestionNotificationFrequency',
        icon: <ChatQuoteFillIcon color={theme.palette.custom.cloverGreen} size={18} />,
        title: 'Assigned to you',
      };
    }

    return list;
  }, [homeModuleFeatures, isUserExist]);

  return (
    <Box>
      <Note>Choose what you would like to get notified for and how often.</Note>
      <Box>
        <Grid container sx={{ alignItems: 'center' }}>
          <Grid item xs={4}></Grid>
          <Grid item xs={5}></Grid>
          <Grid
            item
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}
            xs={3}
          >
            <Box title="Email Notifications" sx={{ mr: 3 }}>
              <MailLineIcon />
            </Box>
            <Box title="Push Notifications" sx={{ mr: 3 }}>
              <SmartphoneLineIcon />
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mt: 2 }}>
        {Object.entries(activeNotificationsList).map(([itemKey, { isActive, icon, key, title }]) =>
          isActive ? (
            <Grid container sx={{ alignItems: 'center', mb: 3 }} key={key}>
              <Grid item xs={4}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {icon}
                  <Typography sx={{ fontWeight: 700, ml: 0.5, mt: -0.2, mr: 0.5 }}>
                    {itemKey}
                  </Typography>
                  {title && (
                    <Tooltip
                      placement="bottom"
                      title={<TooltipMsg fontSize={14}>{title}</TooltipMsg>}
                      PopperProps={{
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, -8],
                            },
                          },
                        ],
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <InformationLineIcon size={18} />
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              </Grid>
              <Grid item xs={5}>
                <Controller
                  control={control}
                  render={({ field: { ref, value, ...field }, fieldState }) => {
                    const selectedOption = useMemo(
                      () => notificationOptions.find((option) => option.value === value) || null,
                      [value],
                    );
                    return (
                      <FormControl fullWidth>
                        <Autocomplete
                          {...field}
                          value={selectedOption}
                          onChange={(event, newValue) => {
                            field.onChange(newValue?.value || '');

                            if (newValue?.value) {
                              setValue(
                                `pushNotificationSettings.${
                                  key as keyof NotificationsForm['pushNotificationSettings']
                                }`,
                                newValue.value !== 'REAL_TIME' ? 'NEVER' : 'REAL_TIME',
                                { shouldDirty: true, shouldValidate: true },
                              );
                              setValue(
                                `emailNotificationSettings.${
                                  key as keyof NotificationsForm['emailNotificationSettings']
                                }`,
                                newValue.value,
                                { shouldDirty: true, shouldValidate: true },
                              );
                            }
                          }}
                          options={
                            key === 'postNotificationFrequency'
                              ? notificationOptions.slice(1)
                              : notificationOptions
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={!!fieldState.error}
                              helperText={fieldState?.error?.message || ''}
                              InputLabelProps={{ shrink: true }}
                              variant="standard"
                            />
                          )}
                          disabled={isSubmitting}
                        />
                      </FormControl>
                    );
                  }}
                  name={`${key as keyof NotificationsForm}`}
                />
              </Grid>
              <Grid
                item
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}
                xs={3}
              >
                <Controller
                  key={`emailNotificationSettings.${key}`}
                  name={`emailNotificationSettings.${
                    key as keyof NotificationsForm['emailNotificationSettings']
                  }`}
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value === 'NEVER' ? false : true}
                          onChange={(e) => {
                            setValue(
                              `emailNotificationSettings.${
                                key as keyof NotificationsForm['emailNotificationSettings']
                              }`,
                              e.target.checked ? notifications[key] : 'NEVER',
                              { shouldDirty: true, shouldValidate: true },
                            );
                          }}
                        />
                      }
                      label={''}
                      disabled={isSubmitting || notifications[key] === 'NEVER'}
                    />
                  )}
                />
                <Controller
                  key={`pushNotificationSettings.${key}`}
                  name={`pushNotificationSettings.${
                    key as keyof NotificationsForm['pushNotificationSettings']
                  }`}
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value === 'REAL_TIME' ? true : false}
                          onChange={(e) => {
                            setValue(
                              `pushNotificationSettings.${
                                key as keyof NotificationsForm['pushNotificationSettings']
                              }`,
                              e.target.checked ? notifications[key] : 'NEVER',
                              { shouldDirty: true, shouldValidate: true },
                            );
                          }}
                        />
                      }
                      label={''}
                      disabled={isSubmitting || notifications[key] !== 'REAL_TIME'}
                    />
                  )}
                />
              </Grid>
            </Grid>
          ) : null,
        )}
      </Box>
      {Object.keys(INTERACTION_NOTIFICATIONS)?.map((key: string) => (
        <Grid container sx={{ alignItems: 'center' }} key={key}>
          <Grid item xs={9}>
            {INTERACTION_NOTIFICATIONS[key]}
          </Grid>
          <Grid item xs={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Controller
              key={`emailNotificationSettings.${key}`}
              name={`emailNotificationSettings.${
                key as keyof NotificationsForm['emailNotificationSettings']
              }`}
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value as boolean} />}
                  label={''}
                  disabled={isSubmitting}
                />
              )}
            />
            <Controller
              key={`pushNotificationSettings.${key}`}
              name={`pushNotificationSettings.${
                key as keyof NotificationsForm['pushNotificationSettings']
              }`}
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value as boolean} />}
                  label={''}
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>
        </Grid>
      ))}
      <Divider sx={{ mt: 1, mb: 1, width: '95%' }} />
      <Box>
        <Note sx={{ fontWeight: 700, fontSize: 16, color: theme.palette.custom.tuna }}>
          Your notifications will be curated based on your selections below.
        </Note>
        <Grid container>
          {requiredInputFields.map((field) => (
            <Grid item xs={11.5}>
              <FieldWrapper
                id={field.entryCode}
                label={field.referenceObject?.label || field.entryDescription}
                required={field.referenceObject?.isRequired}
                disabled={isSubmitting}
              >
                <RenderFilterFieldByType field={field} />
              </FieldWrapper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}

const Note = styled(Typography, { label: 'Note' })(({ theme }) => ({
  marginTop: 16,
  marginBottom: 16,
  fontSize: 16,
  fontWeight: 700,
  color: theme.palette.custom.tuna,
}));

const TooltipMsg = styled(Typography)(({ theme }) => ({
  color: theme.palette.custom.white,
  fontSize: 12,
  fontWeight: 600,
}));

export default InteractionsForm;
